/**
 * Skin NFT API calls.
 *
 * Cosmetics for the OrobasHipercube floating icon: hats, glasses, eye, nose and
 * mouth accessories. Each purchase mints a serial-numbered NFT whose mint, equip
 * and unequip events are recorded on the server's hash-chained ledger.
 *
 * The wire the API answers with is camelCase, like every other route in this
 * bundle: the shapes below are read rather than cast, for the same reason
 * `lib/api.ts`'s order rows are parsed field by field.
 */

import { call } from './api';

export type SkinSlot = 'hat' | 'glasses' | 'eye' | 'nose' | 'mouth' | 'body';

/** A catalogue cosmetic. `isOwned` is false on the public catalogue rows. */
export interface Skin {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly slot: SkinSlot;
  readonly previewUrl: string;
  /** Serialised keyframes for animated slots; null for static ones. */
  readonly anchorData: string | null;
  readonly priceCents: number;
  readonly currency: string;
  readonly rarity: 'common' | 'rare' | 'epic' | 'legendary';
  readonly tags: readonly string[];
  readonly isOwned: boolean;
}

export interface SkinCatalogue {
  readonly skins: readonly Skin[];
}

export interface UserSkinInventory {
  readonly skinId: string;
  readonly slot: SkinSlot;
  readonly isEquipped: boolean;
  /** The minted NFT's serial within its skin; see the ledger endpoint. */
  readonly tokenId: string;
  readonly serialNo: number;
  readonly mintBlockNo: number | null;
}

/** One skin id per slot, null where the slot is bare. */
export interface EquippedSkins {
  readonly hat: string | null;
  readonly glasses: string | null;
  readonly eye: string | null;
  readonly nose: string | null;
  readonly mouth: string | null;
  readonly body: string | null;
}

/** One block of the account's own ledger history. */
export interface LedgerBlock {
  readonly blockNo: number;
  readonly blockHash: string;
  readonly payload: unknown;
  readonly minedAt: string | null;
}

/** The chain's tip and an on-demand verification pass over it. */
export interface ChainStatus {
  readonly head: { readonly blockNo: number; readonly blockHash: string; readonly count: number } | null;
  readonly verified: boolean;
  readonly checkedBlocks: number;
  readonly brokenAt: number | null;
  readonly detail: string;
}

/** Fetch the cosmetic catalogue. Public, like the plan catalogue. */
export function fetchSkins(): Promise<SkinCatalogue> {
  return call<SkinCatalogue>('/skins');
}

/** The signed-in account's minted NFTs. */
export function fetchUserSkins(token: string): Promise<{ skins: readonly UserSkinInventory[] }> {
  return call<{ skins: readonly UserSkinInventory[] }>('/account/skins', {}, token);
}

/** The currently equipped outfit, one skin id per slot. */
export function fetchEquippedSkins(token: string): Promise<{ equipped: EquippedSkins }> {
  return call<{ equipped: EquippedSkins }>('/account/skins/equipped', {}, token);
}

/** Equip or unequip one owned skin. Returns what the wallet now wears. */
export function equipSkin(token: string, skinId: string, equip: boolean): Promise<{ equipped: boolean; slot: string }> {
  return call<{ equipped: boolean; slot: string }>(
    `/account/skins/${encodeURIComponent(skinId)}/equip`,
    { method: 'POST', body: JSON.stringify({ equip }) },
    token
  );
}

/** Places a skin order and returns the payment link. */
export function purchaseSkin(token: string, skinId: string): Promise<{ orderId: string; checkoutUrl: string; amountCents: number; currency: string }> {
  return call<{ orderId: string; checkoutUrl: string; amountCents: number; currency: string }>(
    `/account/skins/${encodeURIComponent(skinId)}/purchase`,
    { method: 'POST' },
    token
  );
}

/** The account's own ledger blocks, newest first. */
export function fetchSkinLedger(token: string, limit: number = 50): Promise<{ blocks: readonly LedgerBlock[] }> {
  return call<{ blocks: readonly LedgerBlock[] }>(`/account/skins/ledger?limit=${limit}`, {}, token);
}

/** The chain's tip and verification state. Public. */
export function fetchChainStatus(): Promise<ChainStatus> {
  return call<ChainStatus>('/skins/chain');
}
